import { keepPreviousData, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronRight, CircleCheck, Search, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import type { HomeAlert, HomeProgress, HomeResponse, SearchGroup, SearchHit, SearchResponse } from "../../shared/home";
import type { ToolMeta } from "../../shared/tools";
import { comingSoonTools, webTools } from "../../tools/web";
import { Icon } from "../components/Icon";
import { SyncBadge } from "../components/SyncBadge";
import { useWithWindows } from "../components/ToolWindows";
import { Badge, PageHeader, Panel, ProgressBar, Spinner } from "../components/ui";
import { api } from "../lib/api";
import { useCompany } from "../lib/company";
import { useMoney } from "../lib/money";
import { useMe, useSm8Query } from "../lib/queries";

/*
 * Home: search across every tool, what needs attention, how far setting up has got, and the tools
 * with a few figures each.
 * Each tool puts its own alerts, figures and results here (its home.ts, via /api/c/:id/home), and
 * they update live with the data they come from.
 */

export function Home() {
    const company = useCompany();
    const { data: me } = useMe();
    const tools = company.tools.map((id) => webTools[id]?.meta).filter((m): m is ToolMeta => m !== undefined);
    const firstName = me?.user.name?.split(" ")[0];
    const [query, setQuery] = useState("");
    const home = useHome(tools);

    return (
        <div className="page">
            <PageHeader title={firstName ? `Hi ${firstName}` : "Home"} subtitle={company.name} />
            {tools.length ? (
                <>
                    <SearchBox query={query} onChange={setQuery} tools={tools} />
                    {query.trim() ? (
                        <SearchResults query={query} tools={tools} />
                    ) : (
                        <div className="grid gap-x-6 lg:grid-cols-3">
                            <div className="lg:col-span-2">
                                <Attention home={home.data} loading={home.isLoading} tools={tools} />
                                <Setup home={home.data} tools={tools} />
                            </div>
                            <ToolCards home={home.data} tools={tools} />
                        </div>
                    )}
                </>
            ) : (
                <Panel>
                    <div className="p-2 text-center text-sm text-muted">No tools yet. They&apos;ll appear here as they&apos;re added.</div>
                </Panel>
            )}
        </div>
    );
}

/** Refetched when any tool's synced data or its own data changes (and when the tab comes back into view). */
function useHome(tools: readonly ToolMeta[]) {
    const company = useCompany();
    const objects = useMemo(() => [...new Set(tools.flatMap((t) => t.datasets ?? []))], [tools]);
    return useSm8Query<HomeResponse>({ companyId: company.id, objects, key: ["home"], path: "/home", alsoOn: tools.map((t) => t.id) });
}

// ---------------------------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------------------------

function SearchBox(props: Readonly<{ query: string; onChange: (q: string) => void; tools: readonly ToolMeta[] }>) {
    const [, navigate] = useLocation();
    return (
        <label className="relative mb-6 block">
            <span className="sr-only">Search</span>
            <Search className="pointer-events-none absolute top-1/2 left-3.5 size-5 -translate-y-1/2 text-muted" />
            <input
                type="search"
                className="input py-3 pl-11 text-base"
                placeholder={searchPlaceholder(props.tools)}
                value={props.query}
                onChange={(e) => props.onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Escape") props.onChange("");
                    // Enter opens the top result (SearchResults marks it).
                    if (e.key === "Enter") {
                        const first = document.querySelector<HTMLAnchorElement>("[data-first-hit]");
                        if (first) navigate(first.getAttribute("href") ?? "/");
                    }
                }}
            />
        </label>
    );
}

const searchPlaceholder = (tools: readonly ToolMeta[]) => (tools.some((t) => t.id === "pricelist") ? "Search items, suppliers and bundles" : "Search");

/** Waits for a pause in typing, so each keystroke doesn't ask the server. */
function useSettled(value: string, ms = 200) {
    const [settled, setSettled] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setSettled(value), ms);
        return () => clearTimeout(timer);
    }, [value, ms]);
    return settled;
}

function SearchResults(props: Readonly<{ query: string; tools: readonly ToolMeta[] }>) {
    const company = useCompany();
    const q = useSettled(props.query.trim());
    const objects = useMemo(() => [...new Set(props.tools.flatMap((t) => t.datasets ?? []))], [props.tools]);
    const { data, isFetching } = useQuery({
        queryKey: ["c", company.id, "home", "search", q],
        queryFn: () => api<SearchResponse>(`/c/${company.id}/home/search?q=${encodeURIComponent(q)}`),
        enabled: q.length > 0,
        placeholderData: keepPreviousData,
        meta: { sm8: objects, alsoOn: props.tools.map((t) => t.id) },
    });
    const hits = data?.hits ?? [];
    const kinds = [...new Set(hits.map((h) => h.kind))];
    const groupOf = (kind: string) => data?.groups.find((g) => g.kind === kind);

    return (
        <Panel title={<h2 className="flex items-center gap-2">Results {isFetching ? <Spinner className="size-4" /> : null}</h2>} flush>
            {hits.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted">{data ? `Nothing matches "${q}".` : "Searching…"}</p>
            ) : (
                kinds.map((kind) => (
                    <div key={kind}>
                        <div className="border-b border-line bg-surface-2 px-4 py-1.5 text-xs font-semibold tracking-wide text-muted uppercase">{kind}s</div>
                        <ul className="m-0 list-none p-0">
                            {hits
                                .filter((h) => h.kind === kind)
                                .map((h) => (
                                    <SearchRow key={h.id} hit={h} first={h === hits[0]} />
                                ))}
                        </ul>
                        <SeeAll group={groupOf(kind)} shown={hits.filter((h) => h.kind === kind).length} query={q} />
                    </div>
                ))
            )}
        </Panel>
    );
}

/** Under a kind's results, when there are more than shown: all of them, in its list, searched the same. */
function SeeAll(props: Readonly<{ group: SearchGroup | undefined; shown: number; query: string }>) {
    const withWindows = useWithWindows();
    const { group } = props;
    if (!group || group.total <= props.shown) return null;
    return (
        <Link href={withWindows(group.href)} className="block border-t border-line px-4 py-2 text-sm text-brand-ink no-underline hover:bg-surface-2">
            See all {group.total.toLocaleString()} {group.kind.toLowerCase()}s matching &ldquo;{props.query}&rdquo; &rarr;
        </Link>
    );
}

/** A result: its window opens over Home (keeping the search, and any other windows open), else it goes to its page. */
function SearchRow({ hit, first }: Readonly<{ hit: SearchHit; first: boolean }>) {
    const fmt = useMoney();
    const [location] = useLocation();
    const search = useSearch();
    let href = hit.href;
    if (hit.opens) {
        const params = new URLSearchParams(search);
        for (const [key, value] of new URLSearchParams(hit.opens)) params.set(key, value);
        href = `${location}?${params}`;
    }
    return (
        <li className="border-b border-line last:border-b-0">
            <Link href={href} data-first-hit={first || undefined} className="flex items-center gap-3 px-4 py-2.5 text-ink no-underline hover:bg-surface-2">
                {hit.synced === null ? null : <SyncBadge synced={hit.synced} />}
                <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{hit.name}</span>
                    {hit.detail ? <span className="block truncate text-xs text-muted">{hit.detail}</span> : null}
                </span>
                {hit.price === undefined ? null : <span className="shrink-0 tabular-nums">{fmt.money(hit.price)}</span>}
                <ChevronRight className="size-4 shrink-0 text-muted" />
            </Link>
        </li>
    );
}

// ---------------------------------------------------------------------------------------------
// Needs attention
// ---------------------------------------------------------------------------------------------

function Attention(props: Readonly<{ home: HomeResponse | undefined; loading: boolean; tools: readonly ToolMeta[] }>) {
    // Red first (costing money, out of date), then amber; each tool's own order within that.
    const alerts = props.tools
        .flatMap((t) => (props.home?.tools[t.id]?.alerts ?? []).map((a) => ({ tool: t, alert: a })))
        .sort((a, b) => Number(b.alert.tone === "red") - Number(a.alert.tone === "red"));
    let body = (
        <ul className="m-0 list-none p-0">
            {alerts.map(({ tool, alert }) => (
                <AlertRow key={`${tool.id}:${alert.id}`} tool={tool} alert={alert} showTool={props.tools.length > 1} />
            ))}
        </ul>
    );
    if (props.loading) body = <div className="grid place-items-center p-6"><Spinner /></div>;
    else if (!alerts.length) {
        body = (
            <p className="flex items-center gap-2 p-4 text-sm text-muted">
                <CircleCheck className="size-5 text-brand-500" /> All caught up: nothing needs looking at.
            </p>
        );
    }
    return (
        <Panel title="Needs attention" flush>
            {body}
        </Panel>
    );
}

function AlertRow({ tool, alert, showTool }: Readonly<{ tool: ToolMeta; alert: HomeAlert; showTool: boolean }>) {
    const withWindows = useWithWindows();
    return (
        <li className="border-b border-line last:border-b-0">
            <Link href={withWindows(alert.href)} className="flex items-center gap-3 px-4 py-3 text-ink no-underline hover:bg-surface-2">
                <TriangleAlert className={clsx("size-5 shrink-0", alert.tone === "red" ? "text-danger" : "text-warning")} />
                <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-semibold">
                            <span className="tabular-nums">{alert.count.toLocaleString()}</span> {alert.label}
                        </span>
                        {showTool ? <Badge>{tool.name}</Badge> : null}
                    </span>
                    {alert.detail ? <span className="block text-xs text-muted">{alert.detail}</span> : null}
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted" />
            </Link>
        </li>
    );
}

// ---------------------------------------------------------------------------------------------
// Setting up
// ---------------------------------------------------------------------------------------------

/** Setup still to do, as bars (each opens the ones left). Gone once everything's done. */
function Setup(props: Readonly<{ home: HomeResponse | undefined; tools: readonly ToolMeta[] }>) {
    const rows = props.tools.flatMap((t) => (props.home?.tools[t.id]?.setup ?? []).map((p) => ({ tool: t, progress: p })));
    if (!rows.length) return null;
    return (
        <Panel title="Setting up" flush>
            <ul className="m-0 list-none p-0">
                {rows.map(({ tool, progress }) => (
                    <SetupRow key={`${tool.id}:${progress.id}`} tool={tool} progress={progress} showTool={props.tools.length > 1} />
                ))}
            </ul>
        </Panel>
    );
}

function SetupRow({ tool, progress, showTool }: Readonly<{ tool: ToolMeta; progress: HomeProgress; showTool: boolean }>) {
    const withWindows = useWithWindows();
    const { done, total } = progress;
    return (
        <li className="border-b border-line last:border-b-0">
            <Link href={withWindows(progress.href)} className="flex items-center gap-3 px-4 py-3 text-ink no-underline hover:bg-surface-2" title={`See the ${(total - done).toLocaleString()} still to do`}>
                <span className="min-w-0 flex-1">
                    <span className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3">
                        <span className="flex items-baseline gap-2 font-semibold">
                            {progress.label}
                            {showTool ? <Badge>{tool.name}</Badge> : null}
                        </span>
                        <span className="text-xs text-muted tabular-nums">
                            {done.toLocaleString()} of {total.toLocaleString()} {progress.unit}
                        </span>
                    </span>
                    <ProgressBar value={done / total} label={`${progress.label}: ${done} of ${total}`} />
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted" />
            </Link>
        </li>
    );
}

// ---------------------------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------------------------

function ToolCards(props: Readonly<{ home: HomeResponse | undefined; tools: readonly ToolMeta[] }>) {
    const withWindows = useWithWindows();
    return (
        <Panel title="Tools" flush>
            <ul className="m-0 list-none p-0">
                {props.tools.map((t) => {
                    const stats = props.home?.tools[t.id]?.stats ?? [];
                    return (
                        <li key={t.id} className="border-b border-line last:border-b-0">
                            <Link href={withWindows(`/t/${t.id}`)} className="flex items-start gap-3 px-4 py-3 no-underline hover:bg-surface-2">
                                <Icon name={t.icon} className="mt-0.5 size-5 shrink-0 text-brand-ink" />
                                <span className="min-w-0 flex-1">
                                    <span className="flex items-center gap-2 font-semibold text-ink">
                                        {t.name}
                                        {t.beta ? <Badge tone="blue">Beta</Badge> : null}
                                    </span>
                                    <span className="block text-xs text-muted">{stats.length ? stats.join(" · ") : t.description}</span>
                                </span>
                            </Link>
                        </li>
                    );
                })}
                {/* Tools on the way: not links. */}
                {comingSoonTools.map((t) => (
                    <li key={t.id} className="flex items-start gap-3 border-b border-line px-4 py-3 last:border-b-0" aria-disabled="true">
                        <Icon name={t.icon} className="mt-0.5 size-5 shrink-0 text-muted" />
                        <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2 font-semibold text-muted">
                                {t.name}
                                <Badge>Coming soon</Badge>
                            </span>
                            <span className="block text-xs text-muted">{t.description}</span>
                        </span>
                    </li>
                ))}
            </ul>
        </Panel>
    );
}
